import { FC, useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, Drawer, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import { GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import CustomDataGrid from "../DataGrid/DataGrid";
import InventoryEmptyState from "./InventoryEmptyState";
import LocationForm from "./LocationForm";
import { FilterBar, FilterSearch, FilterSelect } from "./InvFilters";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchLocations } from "../../store/locationStore/locationThunk";
import { ApiStatus, apiLimit, defaultRowsPerPageOptions } from "../../utils/api.types";
import { LOCATION_TYPES } from "../../utils/location.types";

const LocationsList: FC = () => {
  const dispatch = useAppDispatch();
  const { locations, status, rowCount } = useAppSelector((state) => state.locations);

  // Server-side filters (resolved on the backend via query params).
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [type, setType] = useState("");
  // const [statusFilter, setStatusFilter] = useState("");

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(apiLimit.limit);

  // Right-side drawer form (new / edit), same pattern as Record Expense Payment.
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editLocationId, setEditLocationId] = useState<string | undefined>(undefined);

  const buildParams = () => {
    const p: string[] = [];
    if (debouncedSearch) p.push(`search=${encodeURIComponent(debouncedSearch)}`);
    if (type) p.push(`type=${type}`);
    // if (statusFilter) p.push(`isActive=${statusFilter}`);
    return p.join("&");
  };

  const loadData = () => dispatch(fetchLocations(buildParams()));
  const hasFilters = !!(debouncedSearch || type);
  const resetFilters = () => {
    setSearch("");
    setType("");
  };

  const openNew = () => {
    setEditLocationId(undefined);
    setOpenDrawer(true);
  };
  const openEdit = (id: string) => {
    setEditLocationId(id);
    setOpenDrawer(true);
  };
  const closeDrawer = (refresh: boolean) => {
    setOpenDrawer(false);
    setEditLocationId(undefined);
    if (refresh) loadData();
  };

  // Debounce only the free-text search so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Load when the debounced search or a select changes (selects apply instantly).
  useEffect(() => {
    loadData();
  }, [dispatch, debouncedSearch, type]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 1.5,
        minWidth: 200,
        renderCell: ({ row }: GridValueGetterParams) => {
          const a = row.addressId;
          const addr = a
            ? `${[a.title, a.line1, a.city, a.state?.name].filter(Boolean).join(", ")}${a.code ? ` - ${a.code}` : ""}`
            : "";
          return (
            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", height: "100%", width: "100%", minWidth: 0, overflow: "hidden" }}>
              <span
                style={{ color: "#488dff", cursor: "pointer", lineHeight: 1.3 }}
                onClick={() => openEdit(row._id)}
              >
                {row.name}
              </span>
              {addr && (
                <span
                  title={addr}
                  style={{
                    fontSize: 11,
                    color: "#7a8290",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                    lineHeight: 1.3,
                  }}
                >
                  {addr}
                </span>
              )}
            </Box>
          );
        },
      },
      { field: "code", headerName: "Code", flex: 1, minWidth: 100 },
      { field: "type", headerName: "Type", flex: 1, minWidth: 110 },
      {
        field: "isDefault",
        headerName: "Default",
        flex: 0.7,
        minWidth: 90,
        renderCell: ({ row }: GridValueGetterParams) =>
          row.isDefault ? <Chip size="small" label="Default" sx={{ color: "#49a563" }} /> : null,
      },
      {
        field: "isActive",
        headerName: "Status",
        flex: 0.7,
        minWidth: 90,
        renderCell: ({ row }: GridValueGetterParams) => (
          <Chip
            size="small"
            label={row.isActive ? "Active" : "Inactive"}
            sx={{ color: row.isActive ? "#49a563" : "#c33b3b" }}
          />
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.6,
        minWidth: 90,
        sortable: false,
        renderCell: ({ row }: GridValueGetterParams) => (
          <IconButton size="small" onClick={() => openEdit(row._id)}>
            <EditIcon style={{ fontSize: 16 }} />
          </IconButton>
        ),
      },
    ],
    []
  );

  return (
    <Box sx={{ width: "100%" }}>
      <FilterBar
        onReset={resetFilters}
        action={
          <Button startIcon={<AddIcon />} size="small" variant="contained" onClick={openNew}>
            New Location
          </Button>
        }
      >
        <FilterSearch value={search} onChange={setSearch} placeholder="Search name or code" width={280} />
        <FilterSelect
          value={type}
          onChange={setType}
          placeholder="Type"
          width={180}
          options={LOCATION_TYPES.map((t) => ({ label: t.charAt(0).toUpperCase() + t.slice(1), value: t }))}
        />
        {/* Status filter — hidden for now
        <FilterSelect
          value={statusFilter}
          onChange={setStatusFilter}
          placeholder="Status"
          width={160}
          options={[{ label: "Active", value: "true" }, { label: "Inactive", value: "false" }]}
        />
        */}
      </FilterBar>
      {status !== ApiStatus.LOADING && (locations?.length || 0) === 0 ? (
        hasFilters ? (
          <InventoryEmptyState
            icon={<WarehouseOutlinedIcon />}
            title="No Locations Found"
            description="No locations match the current filters. Try adjusting your search, type, or status."
            caption="Need help? Check the Guide tab on inventory management"
          />
        ) : (
          <InventoryEmptyState
            icon={<WarehouseOutlinedIcon />}
            title="No Locations Yet"
            description="Add your first storage location to start tracking stock by store, kitchen, or warehouse."
            actionLabel="Create Your First Location"
            onAction={openNew}
            caption="Need help? Check the Guide tab on inventory management"
          />
        )
      ) : (
        <div style={{ height: "70vh", width: "100%" }}>
          <CustomDataGrid
            columns={columns}
            rows={locations}
            rowCount={rowCount}
            rowsPerPageOptions={defaultRowsPerPageOptions}
            page={page}
            pageSize={pageSize}
            onPageChange={(p: number) => setPage(p)}
            onPageSizeChange={(s: number) => setPageSize(s)}
            getRowId={(row: any) => row._id || Math.random().toString()}
            loading={status === ApiStatus.LOADING}
          />
        </div>
      )}

      {/* Location Form Drawer (new / edit) */}
      <Drawer disableEscapeKeyDown={false} anchor="right" open={openDrawer} onClose={() => closeDrawer(false)}>
        <LocationForm open={openDrawer} onClose={closeDrawer} locationId={editLocationId} />
      </Drawer>
    </Box>
  );
};

export default LocationsList;
